import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, cacheGet, cacheSet, errText } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { ArrowLeft, Award } from "lucide-react";

export default function Exam() {
  const { id } = useParams();
  const [track, setTrack] = useState(cacheGet(`track_${id}`));
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const { t, pick } = useLang();
  const navigate = useNavigate();

  useEffect(() => {
    if (!track) api.get(`/tracks/${id}`).then((r) => { setTrack(r.data); cacheSet(`track_${id}`, r.data); }).catch(() => {});
  }, [id, track]);

  if (!track) return <p className="text-slate-500">Loading…</p>;
  const exam = track.exam;
  const allAnswered = exam.every((_, i) => answers[i] !== undefined);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tracks/${id}/exam/submit`, { answers: exam.map((_, i) => answers[i]) });
      setResult(data);
      if (data.passed) toast.success(`${t("passed")} — ${data.score}%`);
    } catch (e) { toast.error(errText(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (result) {
    return (
      <div className="max-w-md mx-auto text-center space-y-4 py-10 fade-in">
        <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center text-3xl font-heading font-extrabold ${result.passed ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`} data-testid="exam-score">
          {result.score}%
        </div>
        <h1 className="font-heading text-2xl font-extrabold text-slate-900">{result.passed ? t("passed") : t("failed")}</h1>
        <p className="text-slate-500">{result.correct} / {result.total}</p>
        {result.certificate_id && (
          <Link data-testid="exam-cert-link" to={`/certificate/${result.certificate_id}`}
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-amber-500 text-white font-semibold hover:bg-amber-600">
            <Award size={18} /> {t("viewCertificate")}
          </Link>
        )}
        <div className="flex justify-center gap-3">
          {!result.passed && (
            <button data-testid="retry-exam-btn" onClick={() => { setResult(null); setAnswers({}); }}
              className="h-12 px-6 rounded-xl bg-slate-900 text-white font-semibold">{t("retryExam")}</button>
          )}
          <button data-testid="back-to-track-btn" onClick={() => navigate(`/track/${id}`)}
            className="h-12 px-6 rounded-xl border border-slate-300 text-slate-700 font-semibold">{pick(track.title)}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <Link to={`/track/${id}`} className="inline-flex items-center gap-1 text-sm text-teal-700 font-medium"><ArrowLeft size={16} /> {pick(track.title)}</Link>
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">{t("finalExam")}</h1>
      <p className="text-sm text-slate-500">Pass mark: {track.pass_pct}%</p>
      {exam.map((qz, qi) => (
        <div key={qi} className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
          <p className="font-medium text-slate-800">{qi + 1}. {pick(qz.q)}</p>
          <div className="grid gap-2">
            {pick(qz.options).map((opt, oi) => (
              <button key={oi} data-testid={`exam-q${qi}-opt${oi}`} onClick={() => setAnswers({ ...answers, [qi]: oi })}
                className={`text-left px-4 py-3 rounded-xl border font-medium text-sm transition-colors ${answers[qi] === oi ? "border-teal-500 bg-teal-50 text-teal-800" : "border-slate-200 hover:border-teal-300"}`}>
                {opt}
              </button>
            ))}
          </div>
        </div>
      ))}
      <button data-testid="submit-exam-btn" onClick={submit} disabled={!allAnswered || busy}
        className="w-full sm:w-auto h-12 px-8 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 disabled:opacity-40 transition-colors">
        {t("submitExam")}
      </button>
    </div>
  );
}
